<template>
  <div class="my-10">
    <div class="flex justify-between mb-5">
      <h1 class="text-2xl font-bold">
        {{ $t('page.products') }}
      </h1>
      <div class="flex gap-5 items-center">
        <button
          @click="toggleFilter(true)"
          class="hidden gap-2 items-center text-main-color bg-white p-2 rounded-lg max-lg:flex"
        >
          <IconFilter />
          <p>{{ $t('page.filter') }}</p>
        </button>
        <SortButton
          class="bg-white"
          v-if="currentRelevancy"
          @on-select="handleRelevancy"
          :options="SORT_OPTIONS"
          :defaultValue="currentRelevancy"
        ></SortButton>
      </div>
    </div>
    <div class="grid grid-cols-4 gap-5">
      <div class="col-span-1">
        <MallProductSide
          routeName="products"
          @closeFilter="toggleFilter(false)"
          :filterState="filterState"
          :brands="brands"
          :currentCategories="query.categories"
          :currentSubscription="query.subscription"
          :currentBrands="query.brand"
          :currentPrice="query.price__range"
          :currentIsNew="query.is_new"
          :currentIsPromotion="query.in_promotion"
          :currentHasDiscount="query.has_discount"
          :currentForCredit="query.for_credit"
          :location="getLocation.id"
          :cat="query.cat"
          :subcat="query.subcat"
          :subsubcat="query.subsubcat"
          :hideFilters="['isVip', 'currentSubscription']"
        ></MallProductSide>
      </div>
      <div class="col-span-3 max-lg:col-span-4">
        <div class="grid grid-cols-3 gap-4 max-lg:grid-cols-2">
          <template v-if="products.length > 0">
            <router-link
              v-for="item in products"
              :key="item?.id"
              :to="{ name: 'storeProductDetail', params: { id: item?.id } }"
            >
              <StoreProductCard @likeAction="likeAction" :detail="item"> </StoreProductCard>
            </router-link>
          </template>
          <div v-show="products.length === 0 && loading === false">
            <p class="text-xl font-medium whitespace-nowrap">{{ $t('page.no_products') }}</p>
          </div>
        </div>
        <div v-show="loading === true" class="flex justify-center mt-10" role="status">
          <IconSpinner class="w-12 h-12 mr-2 text-gray-200 animate-spin fill-main-color">
          </IconSpinner>

          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IconSpinner from '@icons/IconSpinner.vue';
import IconFilter from '@icons/IconFilter.vue';
import MallProductSide from '@cmp/mall/MallProductSide.vue';
import StoreProductCard from '@cmp/mall/StoreProductCard.vue';
import api from '@/api';
import SortButton from '@cmp/SortButton.vue';
import { updateLastSeen } from '@/helpers';
import { SORT_OPTIONS } from '@/constants';

export default {
  name: 'products',
  components: {
    StoreProductCard,
    MallProductSide,
    IconSpinner,
    IconFilter,
    SortButton,
  },
  watch: {
    '$route.query': {
      deep: true,
      async handler(newQuery, oldQuery) {
        const categoryChanged = newQuery.categories !== oldQuery?.categories;

        this.query.categories = newQuery.categories;
        this.query.subscription = newQuery.subscription;
        this.query.brand = newQuery.brand;
        this.query.ordering = newQuery.ordering;
        this.query.price__range = newQuery.price__range;
        this.query.is_new = newQuery.is_new;
        this.query.in_promotion = newQuery.in_promotion;
        this.query.has_discount = newQuery.has_discount;
        this.query.for_credit = newQuery.for_credit;
        this.query.location = this.getLocation.id;
        this.query.page = 1;
        this.query.cat = newQuery.cat;
        this.query.subcat = newQuery.subcat;
        this.query.subsubcat = newQuery.subsubcat;

        this.products = [];
        this.next = null;

        this.currentRelevancy =
          this.SORT_OPTIONS.find(el => el.value == this.query.ordering) || null;

        if (categoryChanged) {
          await this.getBrands();
        }

        await this.getProducts();
      },
    },
  },
  data() {
    return {
      loading: false,
      products: [],
      productsRequestKey: 0,
      currentRelevancy: null,
      page: 1,
      count: 0,
      next: null,
      SORT_OPTIONS: SORT_OPTIONS,
      brands: [],
      query: {
        categories: undefined,
        subscription: undefined,
        brand: undefined,
        sort_by: undefined,
        price__range: undefined,
        is_new: undefined,
        in_promotion: undefined,
        has_discount: undefined,
        for_credit: undefined,
        location: undefined,
        page: 1,
        cat: null,
        subcat: null,
        subsubcat: null,
      },

      filterState: false,
    };
  },
  methods: {
    toggleFilter(e) {
      this.filterState = e;
      document.body.style.overflowY = e ? 'hidden' : 'initial';
    },
    infiniteScroll() {
      let scrollTop = document.documentElement.scrollTop;
      let scrollHeight = document.documentElement.scrollHeight;
      let clientHeight = document.documentElement.clientHeight;
      if (scrollTop + clientHeight >= scrollHeight - 650) {
        if (this.next !== null && this.loading === false) {
          this.query.page += 1;
          this.getProducts();
        }
      }
    },
    async getProducts() {
      const requestKey = ++this.productsRequestKey;
      const currentPage = Number(this.query.page || 1);

      this.loading = true;

      try {
        const response = await api.get('mall/products/', {
          params: this.query,
          paramsSerializer: { indexes: null },
        });

        if (requestKey !== this.productsRequestKey) {
          return;
        }

        const results = response.data.results || [];

        if (currentPage === 1) {
          this.products = results;
        } else {
          this.products.push(...results);
        }

        this.count = response.data.count;
        this.next = response.data.next;
      } catch (e) {
        if (requestKey === this.productsRequestKey) {
          console.log(e);
        }
      } finally {
        if (requestKey === this.productsRequestKey) {
          this.loading = false;
        }
      }
    },
    async likeAction(id, like) {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        const response = await api.post(`mall/products/${id}/like/`, { is_liked: like });
        this.$store.getLiked('mall');
      } catch (e) {
        console.log(e);
      }
    },
    async getBrands() {
      try {
        const response = await api.get('mall/brands/', {
          params: { categories: this.query.categories, location: this.getLocation.id },
        });
        this.brands = response.data;
      } catch (e) {
        console.log(e);
      }
    },

    handleRelevancy(e) {
      this.query.ordering = e;
      this.$router.push({
        name: 'products',
        query: this.query,
        paramsSerializer: { indexes: null },
      });
    },
  },
  computed: {
    getLocation() {
      return JSON.parse(localStorage.getItem('selectedLocation')) || 1;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.infiniteScroll);
  },
  unmounted() {
    window.removeEventListener('scroll', this.infiniteScroll);
  },
  async created() {
    ((this.query.categories = this.$route.query.categories),
      (this.query.subscription = this.$route.query.subscription),
      (this.query.brand = this.$route.query.brand),
      (this.query.ordering = this.$route.query.ordering),
      (this.query.price__range = this.$route.query.price__range),
      (this.query.is_new = this.$route.query.is_new),
      (this.query.in_promotion = this.$route.query.in_promotion),
      (this.query.has_discount = this.$route.query.has_discount),
      (this.query.for_credit = this.$route.query.for_credit),
      (this.query.location = this.getLocation.id),
      (this.query.cat = this.$route.query.cat));
    this.query.subcat = this.$route.query.subcat;
    this.query.subsubcat = this.$route.query.subsubcat;
    this.currentRelevancy = this.SORT_OPTIONS.filter(el => el.value == this.query.ordering)[0];
    await this.getBrands();
    await this.getProducts();
    updateLastSeen('mall_products');
  },
};
</script>

<style scoped></style>
